import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, delay, filter, switchMap, tap } from 'rxjs/operators';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasTableHeaderComponent } from '../../gas-table-header/gas-table-header.component';
import { GasFormKeyDirective } from '../../../utils/gas-form-key/gas-form-key.directive';
import { GasTemplateDirective } from '../../../utils/gas-template/gas-template.directive';
import { equals, getNestedObjectValue } from '../../../utils/helpers/object.helper';
import { GasPaginator } from '../../gas-paginator/gas-paginator.namespace';
import { GasTags } from '../../gas-tags/gas-tags.namespace';

import { GasTableService } from '../gas-table.service';
import { GasTable } from '../gas-table.namespace';
import { GasDefaultTableRepository } from '../gas-default-table.repository';
import { GasObjectHelper } from '@cikrf/gas-utils/helpers';
import { GasCustomTableFilterDirective } from '../gas-custom-table-filter/gas-custom-table-filter.directive';
import { GasSimpleTableComponent } from '../gas-simple-table/gas-simple-table.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GasSortingtableHeadRowDirective } from '../gas-table-sorting/gas-sorting-table-head-row.directive';
import TableRowsSelectionMode = GasTable.TableRowsSelectionMode;
import TableValue = GasTable.TableValue;
import TableSelectedValue = GasTable.TableSelectedValue;
import FilterValue = GasTable.FilterValue;
import SortingValue = GasTable.SortingValue;
import FilterButtonCustomFunctions = GasTable.FilterButtonCustomFunctions;

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=1815%3A28896 */
/** @description Отображает данные в табличном формате */
@UntilDestroy()
@Component({
  selector: 'gas-table',
  templateUrl: './gas-table.component.html',
  styleUrls: ['./gas-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GasTableService, DatePipe],
})
export class GasTableComponent<T = unknown> implements OnInit, OnChanges, AfterViewInit, AfterContentInit {
  @Input()
  public tableTitle: string | TemplateRef<ElementRef>;

  @Input()
  public repository: GasDefaultTableRepository;

  @Input()
  public set selectedRows(outerSelectedRows: TableSelectedValue) {
    this.tableSelectedRows = outerSelectedRows;
  }

  public tableSelectedRows: TableSelectedValue;

  @Input()
  public dataKey: string;

  @Input()
  public selectionMode: TableRowsSelectionMode | string = TableRowsSelectionMode.None;

  @Input()
  public disableRowSelectionFilter: ((rowData: Record<string, unknown>) => boolean) | undefined;

  @GasInputBoolean()
  @Input()
  public verticalFilter: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public filterAlwaysVisible = false;

  @GasInputBoolean()
  @Input()
  public hideTableOnClearFilters = false;

  @Input()
  public tableHeaderTotalText: string;

  @GasInputBoolean()
  @Input()
  public hideFooter: string | boolean = false;

  @Input()
  public hideHeaderBar = false;

  @GasInputBoolean()
  @Input()
  public hideSearchFilter: string | boolean = false;

  @Input()
  public actions: GasTable.Action[] = [];

  @Input()
  public hiddenTableColumnsKeys: string[] = [];

  @Input()
  public filterButtonCustomFunctions: FilterButtonCustomFunctions;

  @Input()
  public columnsVisibilityPopupTitle = 'Колонки, показанные в таблице';

  @GasInputBoolean()
  @Input()
  public columnSettingsVisible = false;

  @GasInputBoolean()
  @Input()
  public hideFilterAfterApplying = false;

  @GasInputBoolean()
  @Input()
  public visibleResetColumnButton = false;

  @Input()
  public set textPayload(texts: GasTable.Texts) {
    this.texts = GasObjectHelper.mergeDeep(GasTable.DEFAULT_TEXTS, texts);
  }

  public get textPayload(): GasTable.Texts {
    return this.texts;
  }

  @Output('selectedRowsChange')
  public selectedRowsChange$ = new EventEmitter();

  @Output('rowClicked')
  public rowClicked$ = new EventEmitter();

  @Output('columnViewChanged')
  public columnViewChanged$ = new EventEmitter<Record<string, boolean>>();

  @Output('filterChanged')
  public filterChanged$ = new EventEmitter<FilterValue>();

  @Output('resetColumnManage')
  public resetColumnManage$ = new EventEmitter<void>();

  @ContentChildren(GasTemplateDirective, { descendants: true })
  public gasTemplates: QueryList<GasTemplateDirective>;

  @ContentChildren(GasFormKeyDirective, { descendants: true })
  public gasInputs: QueryList<GasFormKeyDirective>;

  @ContentChildren(GasCustomTableFilterDirective, { descendants: true })
  public gasCustomTableFilter: QueryList<GasCustomTableFilterDirective>;

  @ContentChildren(GasSortingtableHeadRowDirective, { descendants: true })
  public gasCellSortDirectiveList: QueryList<GasSortingtableHeadRowDirective>;

  @ViewChild(GasTableHeaderComponent)
  public gasTableHeaderComponent: GasTableHeaderComponent;

  @ViewChild('simpleTable')
  public simpleTableComponent: GasSimpleTableComponent;

  public headerTemplate: TemplateRef<HTMLElement>;

  public bodyTemplate: TemplateRef<HTMLElement>;

  public manageTemplate: TemplateRef<HTMLElement>;

  public filtersTemplate: TemplateRef<HTMLElement>;

  public filterButtonsTemplate: TemplateRef<HTMLElement>;

  public selectionKeys: Record<string, unknown> = {};

  public texts: GasTable.Texts = GasTable.DEFAULT_TEXTS;

  public get data(): TableValue[] {
    return this.dataSubject$.value;
  }

  public get isValueEmpty(): boolean {
    return this.dataSubject$.value.length === 0;
  }

  public hideTable$ = new BehaviorSubject(true);

  public data$: Observable<TableValue[]>;

  public filterTags: GasTags.GasTag<GasFormKeyDirective[]>[] = [];

  public loading$ = new BehaviorSubject<boolean>(false);
  public error$ = new BehaviorSubject<boolean>(false);
  public clearAllFilters$ = new Subject<void>();
  public clearCurrentFilter$ = new Subject<GasFormKeyDirective | GasFormKeyDirective[]>();
  public bodyRowsChanged$ = new Subject<void>();
  public initColumnSubject$ = new Subject<QueryList<ElementRef>>();
  public updateColumnsVisibilitySubject$ = new BehaviorSubject<boolean>(false);
  public sortingArray$;

  private dateFormats: GasFormKeyDirective[] = [];
  private dataSubject$ = new BehaviorSubject<TableValue[]>([]);

  constructor(
    // tableService Используется в других компонентах таблицы
    public tableService: GasTableService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
  ) {
    this.data$ = this.dataSubject$.asObservable();

    this.data$
      .pipe(
        filter((data: GasTable.TableValue[]) => data.length > 0),
        filter(() => Boolean(this.simpleTableComponent?.tableHeadRow)),
        switchMap(() => this.simpleTableComponent.tableHeadRow.changes),
        untilDestroyed(this),
      )
      .subscribe((list: QueryList<ElementRef>) => {
        if (this.columnSettingsVisible) {
          this.initColumnSubject$.next(list);
        }

        if (this.selectionMode !== TableRowsSelectionMode.None && this.dataSubject$?.value.length > 0) {
          if (!this.dataKey) {
            throw new Error('Свойство dataKey обязательно к заполнению при использовании режима выбора строк таблицы');
          } else if (!this.repository.paginationData.total) {
            throw new Error('Значение total пагинатора не задано');
          } else if (!this.dataSubject$?.value[0]?.hasOwnProperty(this.dataKey)
            || this.dataSubject$?.value[0][this.dataKey] === '') {
            throw new Error('Атрибут dataKey не найден в переданном объекте данных');
          }
        }
      });

    this.tableService.changeSortingSubject
      .pipe(
        untilDestroyed(this),
      ).subscribe((sortingData: SortingValue) => this.handleSortingDataChanged(sortingData));
  }

  public ngOnInit(): void {
    this.initTableData();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectionMode && !changes.selectionMode.firstChange) {
      throw new Error('Атрибут "selectionMode" не является динамически изменяемым');
    }
  }

  /** initFilters - метод инициализации связи полей и обёртки расширенного фильтра */
  public ngAfterViewInit(): void {
    setTimeout(() => {
      /**Проверяем, есть ли пользовательские компоненты, содержащие в себе фильтры */
      if (this.gasCustomTableFilter.length > 0) {
        const customFilterDirectives = this.getCustomControls(this.gasCustomTableFilter);

        //Перезаписываем QueryList имеющимися и кастомными компонентами
        this.gasInputs.reset([...this.gasInputs.toArray(), ...customFilterDirectives]);
      }

      if (this.gasInputs) {
        this.dateFormats = this.gasInputs.filter((gasInput: GasFormKeyDirective) => !!gasInput.dateInfo);
        this.gasTableHeaderComponent.initFilters(this.gasInputs);
      }

      this.filterTags = GasTable.generateTags([...this.gasTableHeaderComponent.gasFiltersDirective.modifiedInputs]);
      this.changeDetectorRef.detectChanges();

      /** заполняем управление колонками доступными записями */
      if (this.columnSettingsVisible && this.simpleTableComponent.tableHeadRow) {
        this.initColumnSubject$.next(this.simpleTableComponent.tableHeadRow);
      }
    });
  }

  public ngAfterContentInit(): void {
    this.sortingArray$ = this.repository.sorting$.pipe(
      tap(sorting => {
        this.gasCellSortDirectiveList.forEach(e => {
          if (sorting && Object.keys(sorting).includes(e.key)) {
            return;
          }
          e.setElementClass({ [e.key]: 'asc' });
        });
      }),
    );

    this.gasTemplates.forEach((gasTemplate: GasTemplateDirective) => {
      switch (gasTemplate.name) {
        case 'header':
          this.headerTemplate = gasTemplate.template;
          break;

        case 'body':
          this.bodyTemplate = gasTemplate.template;
          break;

        case 'filter':
          this.filtersTemplate = gasTemplate.template;
          break;

        case 'filterButtons':
          this.filterButtonsTemplate = gasTemplate.template;
          break;

        case 'manage':
          this.manageTemplate = gasTemplate.template;
          break;
      }
    });
  }

  public updateSelectionKeys(): void {
    if (this.dataKey && this.tableSelectedRows) {
      this.selectionKeys = {};
      if (Array.isArray(this.tableSelectedRows)) {
        this.tableSelectedRows.forEach(rowData => {
          this.selectionKeys[String(getNestedObjectValue(rowData, this.dataKey))] = 1;
        });
      } else {
        this.selectionKeys[String(getNestedObjectValue(this.tableSelectedRows, this.dataKey))] = 1;
      }
    }
  }

  public isSelected(rowData: GasTable.TableValue): boolean {
    if (rowData && this.tableSelectedRows && this.dataKey) {
      return this.selectionKeys[String(getNestedObjectValue(rowData, this.dataKey))] !== undefined;
    }

    return false;
  }

  public findIndexInSelection(rowData: GasTable.TableValue): number {
    return ( this.tableSelectedRows as Array<GasTable.TableValue> )
      .findIndex(selectedItem => equals(rowData, selectedItem));
  }

  public toggleRowsWithCheckbox(isChecked: boolean): void {
    this.tableSelectedRows = isChecked ?
      this.dataSubject$.value.filter(rowData => this.disableRowSelectionFilter ? !this.disableRowSelectionFilter(rowData) : true).slice()
      : [];
    this.updateSelectionKeys();

    this.selectedRowsChange$.emit(this.tableSelectedRows);

    this.tableService.onSelectionChange();
  }

  public selectRow(rowData: TableValue): void {
    const isSelected = this.isSelected(rowData);
    switch (this.selectionMode) {
      case TableRowsSelectionMode.Single:
        this.tableSelectedRows = isSelected ? null : rowData;

        break;
      case TableRowsSelectionMode.Multiple:
      case TableRowsSelectionMode.OnlyBody:
        this.selectRowInMultipleSelectionMode(isSelected, rowData);

        break;
    }
    this.updateSelectionKeys();

    this.selectedRowsChange$.emit(this.tableSelectedRows);

    this.tableService.onSelectionChange();
  }

  public handleSortingDataChanged(sorting: SortingValue) {
    this.repository.sorting = sorting;
  }

  public handleFiltersDataChanged(innerFilter: FilterValue): void {
    this.dateFormats.forEach((gasInput: GasFormKeyDirective) => {
      const value = gasInput.dateInfo.input.value;
      if (innerFilter[gasInput.gasFormKey] && new Date(value).toString() === value) {
        gasInput.dateInfo.gasDate = this.datePipe.transform(innerFilter[gasInput.gasFormKey], gasInput.dateInfo.format);
        gasInput.dateInfo.input.value = gasInput.dateInfo.gasDate;
      }
    });

    this.repository.filter = innerFilter;
    this.handlePaginationDataChanged({ page: 1 });

    /** Устанавливаем измененные фильтры */
    setTimeout(() => {
      const modified = Array.from(new Set(this.gasTableHeaderComponent.gasFiltersDirective.modifiedInputs));
      this.filterTags = GasTable.generateTags([...modified]);
      this.changeDetectorRef.detectChanges();
    });

    this.filterChanged$.emit(innerFilter);
  }

  public handlePaginationDataChanged(paginationPayload: GasPaginator.Data): void {
    this.repository.paginationData = { ...this.repository.paginationData, ...paginationPayload };
    this.repository.refreshSubject$.next();
  }

  public reloadPage(): void {
    document.location.reload();
  }

  public emitClearFilters(): void {
    this.clearAllFilters$.next();
  }

  public rowClick(data: TableValue<T>): void {
    this.rowClicked$.emit(data);
    this.selectRow(data);
  }

  public removeFilter(directives: unknown): void {
    this.clearCurrentFilter$.next(directives as GasFormKeyDirective[]);
  }

  public applyColumnManage(manage: Record<string, boolean>): void {
    this.columnViewChanged$.emit(manage);
  }

  public resetColumnManage(): void {
    this.resetColumnManage$.emit();
  }

  private getCustomControls(gasCustomTableFilters: QueryList<GasCustomTableFilterDirective>): GasFormKeyDirective[] {
    const customFilters: GasFormKeyDirective[] = [];

    gasCustomTableFilters.forEach((filterDirective: GasCustomTableFilterDirective) => {
      filterDirective.filters.forEach((filterControl: GasFormKeyDirective) => customFilters.push(filterControl));
    });

    return customFilters;
  }

  private selectRowInMultipleSelectionMode(isSelected: boolean, rowData: TableValue): void {
    if (isSelected) {
      const selectionIndex = this.findIndexInSelection(rowData);
      this.tableSelectedRows = ( this.tableSelectedRows as Array<TableValue> )
        .filter((val, i) => i !== selectionIndex);
    } else {
      this.tableSelectedRows =
        this.tableSelectedRows ? [...( this.tableSelectedRows as Array<TableValue> ), rowData] : [rowData];
    }

    this.tableSelectedRows = this.tableSelectedRows.filter((row) =>
      this.disableRowSelectionFilter ? !this.disableRowSelectionFilter(row) : true,
    );
  }

  private getSource(): Observable<any> {
    if (this.hideTableOnClearFilters && this.gasTableHeaderComponent?.isAdvancedFilterApplied || !this.hideTableOnClearFilters) {
      return this.repository.getData() || of([]);
    }
    return of([]);
  }

  private initTableData(): void {
    this.repository.refreshSubject$
      .pipe(
        tap(() => {
          this.loading$.next(true);
          this.error$.next(false);
        }),
        tap(() => this.toggleRowsWithCheckbox(false)),
        delay(0),
        switchMap(() => {
          if (this.hideTableOnClearFilters) {
            this.hideTable$.next(!this.gasTableHeaderComponent?.isAdvancedFilterApplied);
          }
          return this.getSource().pipe(
            catchError((e) => {
              this.loading$.next(false);
              this.error$.next(true);

              console.error(e);
              return of([]);
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(
        (value: TableValue[]) => {
          this.loading$.next(false);

          this.dataSubject$.next(value);
          this.repository.updateTableValue(value);
          setTimeout(() => {
            if (this.simpleTableComponent.tableBodyRows.length) {
              this.bodyRowsChanged$.next();
            }

            if (this.simpleTableComponent.tableHeadRow && this.columnSettingsVisible) {
              this.updateColumnsVisibilitySubject$.next(!this.updateColumnsVisibilitySubject$.value);
            }
          });
        },
        () => {
          this.loading$.next(false);
          this.error$.next(true);
        },
      );

    this.repository.sorting$
      .pipe(
        untilDestroyed(this),
      ).subscribe((sorting: SortingValue) => this.tableService.sortingTableRepository(sorting));

    this.repository.selectedRowChanged$.pipe(
      untilDestroyed(this),
    ).subscribe((data: TableValue | TableValue[]) => {
      if (Array.isArray(data)) {
        data.forEach((item: TableValue) => this.selectRow(item));
      } else {
        this.selectRow(data);
      }
    });
  }
}
